import React, { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { css, styled } from "goober";
import { Card } from "clutch/src/Card/Card.jsx";
import { mobile } from "clutch/src/Style/style.mjs";

import DownloadButtonWrapper from "@/app/DownloadButtonWrapper.jsx";
import type { GameMeta } from "@/app/game-definition.mjs";
import appendUtm from "@/marketing/append-utm.mjs";

const Features = styled("div")`
  align-self: stretch;
  padding-inline: var(--sp-4);

  ${mobile} {
    padding-inline: var(--sp-2);
  }

  figure {
    display: contents;
  }

  .features {
    display: flex;
    padding: var(--sp-10) 0 var(--sp-12);
    flex-direction: column;
    align-items: center;
    gap: var(--sp-8);
    align-self: stretch;

    ${mobile} {
      display: none;
    }
  }
  .mobile-features {
    display: none;
    ${mobile} {
      display: flex;
      padding: var(--sp-10) 0;
      flex-direction: column;
      align-items: center;
      gap: var(--sp-14);
      align-self: stretch;

      h3 {
        text-align: center;
      }

      figure {
        margin: 0;
      }

      .feature-block {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: var(--sp-4);
        align-self: stretch;

        img {
          width: 100%;
          height: auto;
        }
      }
    }
  }
`;

const ImgFrame = () => css`
  position: relative;
  width: 100%;
  max-width: 50rem;
  aspect-ratio: 16 / 9;

  img {
    width: 100%;
    height: auto;
    border-radius: var(--card-radii);
  }

  a {
    position: absolute;
    inset: 0;
  }
`;

const FeatureTags = styled("div")`
  display: flex;
  max-width: calc(var(--sp-container) + var(--sp-container-gap));
  padding: 0 var(--sp-4);
  justify-content: center;
  align-items: flex-start;
  align-content: flex-start;
  gap: var(--sp-2);
  flex-wrap: wrap;
`;

const FeatureTag = styled("div")`
  cursor: pointer;
  display: flex;
  padding: var(--sp-2) var(--sp-4);
  align-items: flex-start;
  gap: var(--sp-2_5);
  border-radius: calc(var(--sp-1) * 25);
  border: 1px solid var(--shade2-25, rgba(127, 131, 139, 0.25));
  margin: 1px;
  > span {
    font-family: Inter;
    font-size: 0.73619rem;
    font-style: normal;
    font-weight: 500;
    line-height: 1.262rem; /* 171.429% */
    letter-spacing: -0.00663rem;
    color: var(--shade2, #7f838b);
  }

  &.selected,
  &:hover {
    border: 2px solid var(--shade0-75, rgba(227, 229, 234, 0.75));
    margin: 0px;
    > span {
      color: var(--shade0-75, rgba(227, 229, 234, 0.75));
    }
  }
`;

export default memo(function GameFeatures({
  features,
}: {
  features: GameMeta["features"];
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    setSelected(0);
  }, [setSelected, features]);

  const selectedFeature = features[selected] || features[0];

  const title = t(
    "home:features.title",
    "Unleash Your Winning Potential with Blitz",
  );
  return (
    <Features>
      <div className="features">
        <h2 className="type-large-title--bold">{title}</h2>
        <FeatureTags>
          {features.map((f, i) => (
            <FeatureTag
              key={i}
              onClick={() => {
                setSelected(i);
              }}
              className={selected === i ? "selected" : ""}
            >
              <span>{t(...f.title)}</span>
            </FeatureTag>
          ))}
        </FeatureTags>
        <figure>
          <Card padding="0" classNameOuter={ImgFrame()} as="div">
            <img
              src={selectedFeature.image}
              width="880"
              height="493"
              loading="lazy"
              alt={`${t(...selectedFeature.title)} feature preview`}
            />
            {!!selectedFeature.link?.href && (
              <a
                href={appendUtm(selectedFeature.link.href, {
                  utm_source: "blitz",
                  utm_medium: "feature_tab",
                  utm_campaign: "landing",
                })}
              >
                <span className="visually-hidden">
                  {t(...(selectedFeature.link.text ?? selectedFeature.title))}
                </span>
              </a>
            )}
          </Card>
          <figcaption className="type-subtitle1 shade0">
            {t(...selectedFeature.description)}
          </figcaption>
        </figure>

        <DownloadButtonWrapper
          className="desktop-only"
          size="large"
          emphasis="high"
          textClass="type-form--button"
          uiLocation="GAME_FEATURES"
          target="_blank"
        >
          {t("common:downloadBlitzFree", "Download Blitz for Free")}
        </DownloadButtonWrapper>
      </div>
      <div className="mobile-features">
        <h3 className="type-h6">{title}</h3>
        {features.map((f, i) => (
          <figure className="feature-block" key={i}>
            <Card padding="0" classNameOuter={ImgFrame()} as="div">
              <img src={f.image} width="880" height="493" />
            </Card>
            <figcaption className="type-subtitle1 shade0">
              {t(...f.description)}
            </figcaption>
          </figure>
        ))}
      </div>
    </Features>
  );
});
